/* Breakz — a number that counts up when it scrolls into view.

   The motion follows the reference picked for the About teaser,
   codepen.io/shvvffle/pen/JRrLqG: from 0 to the target, linear, three
   seconds, whole numbers only. That pen starts on page load; here the count
   starts once the number is on screen, because the facts sit near the foot
   of the home page and a count that ends before anyone scrolls there is
   never seen.

   The server renders the final value, so without JavaScript, and for search
   engines, the fact reads exactly as typed in the editor. Only the leading
   number counts; the text around it ("%", "dager") stays. A value with no
   number ("Live") or with a decimal ("4,9") is left as it is. When the count
   ends, the element puts the original text back, so the finished DOM is the
   one the server sent.

   Screen readers get the final value from a visually hidden copy during the
   count; the ticking digits are aria-hidden, so nobody hears "37 %".

   ZERO LAYOUT SHIFT, AND WHY IT TAKES THREE SPANS. When this was written the
   self-hosted DM Sans had no `tnum` feature, so `tabular-nums` did nothing and
   digits differed in width ("1" is half a "0"): "10" was wider than "14". The
   font carries a `tnum` feature since 14.09.2026, which steadies the digits;
   the spans stay, because a count from "0" to "100" still changes how many
   digits there are. So:
   - an invisible copy of the final number holds the width, and the text
     after it sits exactly where the server put it;
   - the ticking digits are absolutely positioned over that copy in a box
     that never changes size, right-aligned, so a wider intermediate value
     spills to the left instead of pushing the suffix. A box that resized
     with its text would itself be reported as a shift.
   Measured on the sandbox: 0 layout shift from the facts at 390 and 1280.

   Under prefers-reduced-motion: reduce the final value is shown and nothing
   counts — a separate implementation, not a slower one.

   ONCE PER VISIT. The motion card asks that motion stay good on the two
   hundredth visit. A three-second count is worth seeing once; replayed every
   time someone comes back to the home page in the same visit, it is a
   number that makes you wait. So a fact counts the first time it is seen in
   a browser session, and after that shows its final value at once. The
   memory is sessionStorage, keyed by page path and the fact's text, so a new
   value typed in the editor counts again. In the theme editor it always
   counts, so the setting can be previewed. */

(() => {
  if (customElements.get('breakz-count-up')) return;

  const DURATION = 3000;
  const SEEN_KEY = 'bx-count-up-seen';

  const seenList = () => {
    try {
      const list = JSON.parse(sessionStorage.getItem(SEEN_KEY));
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  };

  const remember = (key) => {
    try {
      const list = seenList();
      if (list.includes(key)) return;
      list.push(key);
      sessionStorage.setItem(SEEN_KEY, JSON.stringify(list.slice(-50)));
    } catch (e) {
      /* storage blocked: the count simply plays again next time */
    }
  };

  // A whole number, optionally grouped the Norwegian way ("2 500"), not
  // followed by a decimal part or by more digits.
  const PATTERN = /^(\D*)(\d{1,3}(?:[  ]\d{3})+|\d+)(?![.,\s]?\d)([\s\S]*)$/;

  class BreakzCountUp extends HTMLElement {
    connectedCallback() {
      if (this.digits) return;
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (!('IntersectionObserver' in window)) return;

      this.original = this.textContent;
      const match = this.original.trim().match(PATTERN);
      if (!match) return;

      this.key = location.pathname + '|' + this.original.trim();
      const editor = window.Shopify && window.Shopify.designMode;
      if (!editor && seenList().includes(this.key)) return;

      const [, before, number, after] = match;
      this.separator = (number.match(/[  ]/) || [''])[0];
      this.target = parseInt(number.replace(/\D/g, ''), 10);

      const readable = document.createElement('span');
      readable.className = 'visually-hidden';
      readable.textContent = this.original.trim();

      const box = document.createElement('span');
      box.style.cssText = 'position:relative;display:inline-block';
      const ghost = document.createElement('span');
      ghost.style.cssText = 'visibility:hidden';
      ghost.textContent = number;
      this.digits = document.createElement('span');
      this.digits.style.cssText =
        'position:absolute;top:0;right:0;left:-10em;text-align:end;white-space:nowrap;pointer-events:none';
      this.text = document.createTextNode('0');
      this.digits.append(this.text);
      box.append(ghost, this.digits);

      const shown = document.createElement('span');
      shown.setAttribute('aria-hidden', 'true');
      shown.append(before, box, after);

      this.replaceChildren(readable, shown);

      // Starts when the number is above the bottom 15% of the viewport, so the
      // count is already under way when the eye reaches it.
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.count();
        },
        { rootMargin: '0px 0px -15% 0px' }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      // Moved or removed mid-count (the theme editor does both): settle on the
      // final text rather than freeze on a partial number.
      if (this.observer) this.observer.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.finish();
    }

    count() {
      remember(this.key);
      let start = 0;
      let last = 0;
      const tick = (now) => {
        if (!start) start = now;
        const progress = Math.min((now - start) / DURATION, 1);
        const value = Math.floor(this.target * progress);
        if (value !== last) {
          // The same text node, its data changed — not textContent, which
          // replaces the node on every frame. On a desktop that insertion
          // re-checked the page's body:has() rules and restyled the whole
          // document each frame of the count: traced on 14.09.2026, scrolling
          // the bottom of the home page, style recalculation ~200 ms per
          // 1000 px scrolled with the node replaced, ~16 ms with it updated.
          this.text.data = this.format(value);
          last = value;
        }
        if (progress < 1) {
          this.frame = requestAnimationFrame(tick);
        } else {
          this.frame = 0;
          this.finish();
        }
      };
      this.frame = requestAnimationFrame(tick);
    }

    finish() {
      if (!this.digits) return;
      this.replaceChildren(this.original);
      this.digits = null;
    }

    format(value) {
      const digits = String(value);
      return this.separator ? digits.replace(/\B(?=(\d{3})+(?!\d))/g, this.separator) : digits;
    }
  }

  customElements.define('breakz-count-up', BreakzCountUp);
})();
